import { ipaSdk } from '@investec/ipa-sdk';
import { useState, useEffect } from 'react';

// Authentication service
export class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private tokenListeners: ((token: string | null) => void)[] = [];
  private tokenRefreshInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.initializeIpaSdk();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private initializeIpaSdk(): void {
    console.log('Initializing IPA SDK');
    
    // Define the redirect URI explicitly to match what's registered in Azure Portal
    const redirectUri = window.location.origin;
    console.log('Using redirect URI:', redirectUri);
    
    // Initialize the SDK with the App ID and environment
    ipaSdk.platform.init(
      'b8e7aeb2-03b9-4a21-935c-52d9b4b6aaeb', // AD App ID
      'dev'
    ).subscribe(
      res => {
        if (res?.result?.account.idToken) {
          this.setToken(res?.result?.account.idToken);
          console.log('Authentication successful');
          
          // Set up token refresh mechanism
          this.setupTokenRefresh();
        } else {
          console.error('Authentication failed: No token in response');
        }
      },
      error => {
        console.error('Authentication error:', error);
        
        // Attempt to handle the error by re-initializing after a delay
        setTimeout(() => {
          console.log('Retrying authentication...');
          this.initializeIpaSdk();
        }, 5000);
      }
    );
  }

  private setupTokenRefresh(): void {
    // Clear any existing refresh interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }
    
    // Set up a new refresh interval (refresh every 25 minutes to be safe)
    this.tokenRefreshInterval = setInterval(() => {
      console.log('Refreshing authentication token');
      
      // For token refresh, use the init method again
      ipaSdk.platform.init(
        'b8e7aeb2-03b9-4a21-935c-52d9b4b6aaeb',
        'dev'
      ).subscribe(
        (res: any) => {
          if (res?.result?.account.idToken) {
            this.setToken(res?.result?.account.idToken);
            console.log('Token refresh successful');
          } else {
            console.error('Token refresh failed: No token in response');
          }
        },
        (error: any) => {
          console.error('Token refresh failed:', error);
          
          // If refresh fails, try to re-initialize
          this.initializeIpaSdk();
        }
      );
    }, 25 * 60 * 1000); // 25 minutes
  }

  public setToken(token: string | null): void {
    this.token = token;
    this.notifyListeners();
  }

  public getToken(): string | null {
    return this.token;
  }

  public addTokenListener(listener: (token: string | null) => void): void {
    this.tokenListeners.push(listener);
    
    // Immediately notify with current token state
    if (this.token) {
      listener(this.token);
    }
  }

  public removeTokenListener(listener: (token: string | null) => void): void {
    this.tokenListeners = this.tokenListeners.filter(l => l !== listener);
  }

  private notifyListeners(): void {
    this.tokenListeners.forEach(listener => listener(this.token));
  }
  
  // Clean up resources
  public cleanup(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }
}

// Custom hook for feature state
export function useFeatureState(featureKey: string) {
  const [featureState, setFeatureState] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let subscription: any;
    
    // Get initial state
    ipaSdk.data.getFeatureContext(featureKey)
      .then(res => {
        setFeatureState(res.result);
        setLoading(false);
      })
      .catch(err => {
        setError(err as Error);
        setLoading(false);
      });
    
    // Subscribe to changes
    subscription = ipaSdk.data.listenForFeatureContextChanges(featureKey)
      .subscribe(
        res => {
          setFeatureState(res.result);
        },
        err => {
          setError(err as Error);
        }
      );
    
    // Cleanup
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [featureKey]);

  // Function to update feature state
  const updateFeatureState = (data: any) => {
    return ipaSdk.platform.updateSingleFeatureContext({
      keyName: featureKey,
      payload: data
    });
  };

  return { featureState, loading, error, updateFeatureState };
}

// Custom hook for active client
export function useActiveClient() {
  const [activeClient, setActiveClient] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let subscription: any;
    
    // Get initial active client
    ipaSdk.data.getActiveClient()
      .then(res => {
        setActiveClient(res.result);
        setLoading(false);
      })
      .catch(err => {
        setError(err as Error);
        setLoading(false);
      });
    
    // Subscribe to changes
    subscription = ipaSdk.data.listenForActiveClientContextChanges()
      .subscribe(
        res => {
          setActiveClient(res.result);
        },
        err => {
          setError(err as Error);
        }
      );
    
    // Cleanup
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Function to set active client
  const setClient = (clientData: any) => {
    return ipaSdk.data.setActiveClient(clientData);
  };

  return { activeClient, loading, error, setClient };
}

// API interceptor for React/Fetch
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const authService = AuthService.getInstance();
  const token = authService.getToken();
  
  const authOptions: RequestInit = {
    ...options,
    headers: {
      ...options.headers as Record<string, string>,
      'Authorization': `Bearer ${token || ''}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for CORS requests
  };
  
  try {
    const response = await fetch(url, authOptions);
    
    if (response.status === 401) {
      // Handle unauthorized - try to refresh the token
      console.warn('Authentication error: Unauthorized. Attempting to refresh token...');
      
      // Attempt to refresh the token
      try {
        await new Promise<void>((resolve, reject) => {
          // Use init method for token refresh
          ipaSdk.platform.init(
            'b8e7aeb2-03b9-4a21-935c-52d9b4b6aaeb',
            'dev'
          ).subscribe(
            (res: any) => {
              if (res?.result?.account.idToken) {
                authService.setToken(res.result.account.idToken);
                console.log('Token refresh successful');
                resolve();
              } else {
                console.error('Token refresh failed: No token in response');
                reject(new Error('Token refresh failed'));
              }
            },
            (error: any) => {
              console.error('Token refresh failed:', error);
              reject(error);
            }
          );
        });
        
        // Retry the original request with the new token
        const newToken = authService.getToken();
        const retryOptions: RequestInit = {
          ...authOptions,
          headers: {
            ...authOptions.headers as Record<string, string>,
            'Authorization': `Bearer ${newToken || ''}`,
          },
        };
        
        return fetch(url, retryOptions);
      } catch (refreshError) {
        console.error('Failed to refresh authentication token:', refreshError);
      }
    }
    
    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}; 